export interface SessionUser {
  id: string;
  username: string;
  displayName: string;
}

export type SessionResult =
  | { status: 'authenticated'; user: SessionUser }
  | { status: 'anonymous' }
  | { status: 'unavailable'; message: string };

interface SessionResponse {
  user: SessionUser;
}

interface ErrorResponse {
  error?: string;
}

export async function readError(response: Response): Promise<string> {
  try {
    const data = (await response.json()) as ErrorResponse;
    return data.error ?? 'Something went wrong. Please try again.';
  } catch {
    return 'Something went wrong. Please try again.';
  }
}

export async function getSession(): Promise<SessionResult> {
  try {
    const response = await fetch('/api/auth/session', {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });

    if (response.status === 401) {
      return { status: 'anonymous' };
    }

    if (!response.ok) {
      return {
        status: 'unavailable',
        message: await readError(response),
      };
    }

    const data = (await response.json()) as SessionResponse;
    rememberOfflineUser(data.user);
    return { status: 'authenticated', user: data.user };
  } catch {
    const offlineUser = readOfflineUser();
    if (typeof navigator !== 'undefined' && !navigator.onLine && offlineUser) {
      return { status: 'authenticated', user: offlineUser };
    }
    return {
      status: 'unavailable',
      message: 'The private closet could not be reached. Check your connection and try again.',
    };
  }
}

export async function login(username: string, password: string): Promise<SessionUser> {
  const response = await fetch('/api/auth/login', {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ username, password }),
  });

  if (!response.ok) {
    throw new Error(await readError(response));
  }

  const data = (await response.json()) as SessionResponse;
  rememberOfflineUser(data.user);
  return data.user;
}

export async function logout(): Promise<void> {
  const response = await fetch('/api/auth/logout', {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error(await readError(response));
  }
}
import { readOfflineUser, rememberOfflineUser } from './offlineCloset';
